import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import axiosInstance from "../../../../../AxiosInstance";
import usePolling from "../../../../../hooks/usePolling";
import { formatTimestamp } from "../../../../../utils/utils";
import styles from "./history_section.module.css";

const POLL_MS = 15000;
// Página en vivo (se refresca sola) y páginas anteriores con "Ver anteriores".
const PAGE = 30;

function describe(entry, t) {
  const key = `history.actions.${entry.action}`;
  const label = t(key);
  const p = entry.payload || {};
  const bits = [];
  if (entry.action === "output") {
    bits.push(t("history.port", { n: p.number }), p.status ? t("history.on") : t("history.off"));
  } else if (entry.action === "output_reset") {
    bits.push(t("history.port", { n: p.number }));
  } else if (entry.action === "output_settings") {
    bits.push(t("history.port", { n: p.number }), `${p.amp_limit} A`, `${p.shutdown_seconds} s`);
  } else if (entry.action === "telemetry_config" && p.interval_seconds !== undefined) {
    bits.push(`${p.interval_seconds} s`);
  } else if (entry.action === "network_config") {
    bits.push(String(p.type || "").toUpperCase(), p.ssid || p.apn || (p.dhcp ? "DHCP" : p.ip || ""));
  } else if (entry.action === "network_probe") {
    bits.push(p.kind || "quick");
  } else if (entry.action === "update_name") {
    bits.push(p.name || "");
  } else if (entry.action === "user_role") {
    bits.push(p.user || "", `${p.from} → ${p.to}`);
  }
  return { label: label === key ? entry.action : label, detail: bits.filter(Boolean).join(" · ") };
}

/**
 * Historial de comandos del equipo: quién mandó qué y cuándo (CommandLog en
 * la nube). Es la auditoría que faltaba para un equipo de seguridad: una
 * salida apagada o un reinicio quedan con nombre y hora.
 */
export default function HistorySection({ pdui_id }) {
  const { t } = useTranslation();
  const [entries, setEntries] = useState(null);
  const [error, setError] = useState(false);
  const [older, setOlder] = useState([]);
  const [exhausted, setExhausted] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  usePolling(
    (isActive) =>
      axiosInstance
        .get(`pdui/${pdui_id}/command-log/?limit=${PAGE}`)
        .then((response) => {
          if (!isActive()) return;
          setEntries(response.data.entries || []);
          setError(false);
        })
        .catch(() => {
          if (isActive()) setError(true);
        }),
    POLL_MS,
    true,
    pdui_id,
  );

  useEffect(() => {
    setOlder([]);
    setExhausted(false);
  }, [pdui_id]);

  const seen = new Set();
  const all = [...(entries || []), ...older].filter((e) => {
    if (seen.has(e.id)) return false;
    seen.add(e.id);
    return true;
  });
  const hasMore = !exhausted && (entries || []).length >= PAGE;

  const loadMore = async () => {
    const last = all[all.length - 1];
    if (!last || loadingMore) return;
    setLoadingMore(true);
    try {
      const r = await axiosInstance.get(
        `pdui/${pdui_id}/command-log/?limit=${PAGE}&before_id=${last.id}&before=${encodeURIComponent(last.created_at)}`,
      );
      const page = r.data.entries || [];
      setOlder((prev) => [...prev, ...page]);
      if (page.length < PAGE) setExhausted(true);
    } catch (e) {
      // se puede volver a intentar
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>{t("history.title")}</div>
      <div className={styles.help}>{t("history.help")}</div>
      {error && <div className={styles.error}>{t("history.error")}</div>}
      {entries && all.length === 0 && <div className={styles.empty}>{t("history.empty")}</div>}
      {entries && all.length > 0 && (
        <div className={styles.list}>
          {all.map((e) => {
            const { label, detail } = describe(e, t);
            return (
              <div key={e.id} className={styles.row}>
                <div className={styles.when}>{formatTimestamp(e.created_at)}</div>
                <div className={styles.main}>
                  <div className={styles.action}>{label}</div>
                  {detail && <div className={styles.detail}>{detail}</div>}
                </div>
                <div className={styles.who}>
                  <div>{e.user_name || e.user_email || t("history.system")}</div>
                  {e.user_name && e.user_email && <div className={styles.email}>{e.user_email}</div>}
                </div>
              </div>
            );
          })}
        </div>
      )}
      {(hasMore || older.length > 0) && (
        <div className={styles.loadMoreRow}>
          {hasMore ? (
            <button type="button" className={styles.loadMore} onClick={loadMore} disabled={loadingMore}>
              {loadingMore ? t("history.loadingMore") : t("history.loadMore")}
            </button>
          ) : (
            <div className={styles.noMore}>{t("history.noMore")}</div>
          )}
        </div>
      )}
    </div>
  );
}
